export type ProfileThemeKey = 'sunrise' | 'evergreen' | 'midnight' | 'blueprint';

export interface ProfileTheme {
  key: ProfileThemeKey;
  name: string;
  description: string;
  backgroundClass: string;
  panelClass: string;
  accentClass: string;
  accentTextClass: string;
  borderClass: string;
}

export const PROFILE_THEMES: Record<ProfileThemeKey, ProfileTheme> = {
  sunrise: {
    key: 'sunrise',
    name: 'Sunrise',
    description: 'Soft amber and shell gradients with cocoa typography.',
    backgroundClass: 'bg-gradient-to-b from-amber-100 via-rose-50 to-white',
    panelClass: 'bg-white/80 text-cocoa',
    accentClass: 'bg-amber-500 text-white',
    accentTextClass: 'text-amber-600',
    borderClass: 'border-amber-200',
  },
  evergreen: {
    key: 'evergreen',
    name: 'Evergreen',
    description: 'Forest greens with cream highlights for calm storytelling.',
    backgroundClass: 'bg-gradient-to-b from-emerald-100 via-emerald-50 to-white',
    panelClass: 'bg-white/85 text-emerald-950',
    accentClass: 'bg-emerald-600 text-white',
    accentTextClass: 'text-emerald-600',
    borderClass: 'border-emerald-200',
  },
  midnight: {
    key: 'midnight',
    name: 'Midnight',
    description: 'Deep indigo with lavender accents for a modern tech feel.',
    backgroundClass: 'bg-gradient-to-b from-slate-900 via-indigo-900 to-slate-800 text-slate-100',
    panelClass: 'bg-slate-800/80 text-slate-100',
    accentClass: 'bg-indigo-500 text-white',
    accentTextClass: 'text-indigo-300',
    borderClass: 'border-indigo-700',
  },
  blueprint: {
    key: 'blueprint',
    name: 'Blueprint',
    description: 'Crisp blues with subtle grid textures for builders and makers.',
    backgroundClass: 'bg-gradient-to-b from-sky-100 via-sky-50 to-white',
    panelClass: 'bg-white/90 text-sky-950',
    accentClass: 'bg-sky-600 text-white',
    accentTextClass: 'text-sky-600',
    borderClass: 'border-sky-200',
  },
};

export const DEFAULT_PROFILE_THEME: ProfileTheme = PROFILE_THEMES.sunrise;

export function getProfileTheme(key?: string | null): ProfileTheme {
  if (!key) return DEFAULT_PROFILE_THEME;
  const normalized = key.trim().toLowerCase() as ProfileThemeKey;
  return PROFILE_THEMES[normalized] ?? DEFAULT_PROFILE_THEME;
}

export function listProfileThemes(): ProfileTheme[] {
  return Object.values(PROFILE_THEMES);
}

// Backward-compatible aliases for sponsor imports
export type SponsorThemeKey = ProfileThemeKey;
export type SponsorTheme = ProfileTheme;
export const SPONSOR_THEMES = PROFILE_THEMES;
export const DEFAULT_SPONSOR_THEME = DEFAULT_PROFILE_THEME;
export const getSponsorTheme = getProfileTheme;
export const listSponsorThemes = listProfileThemes;
